import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import {
  disableNotifications,
  enableNotifications,
  isPushEnabledFor,
  isSubscribedTo,
  pushCapability,
} from '../utils/pushNotifications';
import { PUSH_CHANGED_EVENT, rememberOptOut } from './NotificationPrompt';

/**
 * One row in the venue drawer: lets a guest turn this venue's notifications on
 * or off. It stays hidden when the device cannot receive them at all, and tells
 * iPhone guests to add the site to their Home Screen first.
 *
 * Props: venueId, venueName, color (text/icon colour of the drawer),
 *        onNeedInstall (opens the existing "Add to Home Screen" help).
 */
const VenueNotificationToggle = ({ venueId, venueName, color, onNeedInstall }) => {
  // 'checking' | 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on' | 'working' | 'unavailable'
  const [state, setState] = useState('checking');
  const [message, setMessage] = useState('');
  const refreshVersion = useRef({ value: 0 });

  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current.value;
    setMessage('');
    setState(current => current === 'checking' ? 'checking' : 'working');
    // Only an explicit yes may expose notification controls or a permission prompt.
    let enabled = null;
    try {
      enabled = await isPushEnabledFor(venueId);
    } catch {
      // Keep the feature hidden when its rollout status cannot be confirmed.
    }
    if (version !== refreshVersion.current.value) return;
    if (enabled !== true) {
      setState('unsupported');
      return;
    }
    const cap = pushCapability();
    if (cap.state !== 'ready') {
      setState(cap.state);
      return;
    }
    let subscribed = null;
    try {
      subscribed = await isSubscribedTo(venueId);
    } catch {
      // A failed status check must not imply that notifications are off.
    }
    if (version !== refreshVersion.current.value) return;
    setState(subscribed === null ? 'unavailable' : subscribed ? 'on' : 'off');
  }, [venueId]);

  useEffect(() => {
    const counter = refreshVersion.current;
    if (venueId) refresh();
    return () => { counter.value++; };
  }, [venueId, refresh]);

  // The first-visit prompt can turn notifications on; show that here too.
  useEffect(() => {
    if (!venueId) return undefined;
    window.addEventListener(PUSH_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PUSH_CHANGED_EVENT, refresh);
  }, [venueId, refresh]);

  if (!venueId || state === 'checking' || state === 'unsupported') return null;

  const label = venueName ? `Updates from ${venueName}` : 'Updates from this venue';

  const turnOn = async () => {
    const version = ++refreshVersion.current.value;
    setMessage('');
    setState('working');
    let result;
    try {
      result = await enableNotifications(venueId);
    } catch (error) {
      result = { ok: false, reason: 'unavailable' };
    }
    if (version !== refreshVersion.current.value) return;
    if (result.ok) {
      setState('on');
      return;
    }
    if (result.reason === 'blocked') setState('blocked');
    else if (result.reason === 'needs-install') setState('needs-install');
    else if (result.reason === 'dismissed') {
      setState('off');
      setMessage('Notifications were not turned on.');
    } else await refresh();
  };

  const turnOff = async () => {
    const version = ++refreshVersion.current.value;
    setMessage('');
    setState('working');
    try {
      await disableNotifications(venueId);
      // They chose this: the first-visit prompt must not offer it again on their next visit.
      rememberOptOut(venueId);
      if (version !== refreshVersion.current.value) return;
      setState('off');
    } catch (e) {
      if (version !== refreshVersion.current.value) return;
      // The server may have committed the change before its response was lost.
      await refresh();
    }
  };

  let title = label;
  let hint = '';
  let action = null;
  let Icon = FaBell;
  if (state === 'on') {
    hint = 'On. Tap to turn off.';
    action = turnOff;
  } else if (state === 'off') {
    hint = 'Tap to get news and offers from this venue as notifications.';
    action = turnOn;
  } else if (state === 'unavailable') {
    hint = 'Could not check notification settings. Tap to try again.';
    action = refresh;
  } else if (state === 'working') {
    hint = 'One moment…';
  } else if (state === 'blocked') {
    Icon = FaBellSlash;
    hint = 'Notifications are blocked for this site. Allow them in your browser settings to turn this on.';
  } else if (state === 'needs-install') {
    hint = 'On iPhone, first add this page to your Home Screen, then open it from there and turn this on.';
    action = onNeedInstall || null;
  }

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={title}
      aria-disabled={!action}
      data-testid="notification-toggle"
      className={`text-lg pt-3 pb-3 flex items-center gap-4 border-b-2 ${action ? 'cursor-pointer' : ''}`}
      onClick={() => action && action()}
      onKeyDown={(e) => {
        if (action && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          action();
        }
      }}
    >
      <Icon className="w-6 h-6 flex-none" style={{ color }} />
      <span className="grow">
        <p style={{ color }} className="font-semibold">{title}</p>
        <p style={{ color }} className="text-sm opacity-80" data-testid="notification-hint">
          {message || hint}
        </p>
      </span>
    </span>
  );
};

const NotificationToggle = props => <VenueNotificationToggle key={props.venueId} {...props} />;

export default NotificationToggle;
